import { PropsWithChildren } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { AtlassianLogo } from '@atlaskit/logo';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

export const DeveloperBundleTemplate = ({ children }: PropsWithChildren) => (
	<div>
		<div css={headerCss}>
			<AtlassianLogo size="large" appearance="brand" />
		</div>
		<div css={rowCss}>
			<div css={columnCss}>
				<h1 css={headingCss}>
					<FormattedMessage
						id="WacSignupForm.sideInfoHeaderBundleDeveloperMode"
						defaultMessage="Cloud Developer Bundle"
					/>
				</h1>
				<h2 css={subHeadingCss}>
					<FormattedMessage id="WacSignupHeader.cloudStandard" defaultMessage="Cloud Standard" />
				</h2>
				<FormattedMessage
					id="signup.developerBundle.sideInfoMessage"
					defaultMessage="<p>You are signing up for free development sites of Atlassian cloud products. This gives you access to the standard versions of Jira Software, Jira Service Management, and Confluence, which are free forever.</p><p>Free development sites are intended to be used for development and testing purposes only and have the usage limits noted below. Atlassian does not offer support for free development sites and reserves the right to delete such sites at any time without notice. Atlassian monitors the usage of such development sites and may cancel any site being used in an unauthorized or unintended manner.</p><ul><li>Confluence: 5 users free</li><li>Jira Software: 5 users free</li><li>Service Collection: 1 agent free</li><li>All products: Up to 250 GB of file storage</li><li>All products: Advanced permissions</li></ul>"
					values={renderHtml}
				/>
			</div>
			<div css={columnCss}>{children}</div>
		</div>
	</div>
);

const renderHtml = {
	p: (chunks: React.ReactNode) => <p>{chunks}</p>,
	ul: (chunks: React.ReactNode) => <ul css={ulCss}>{chunks}</ul>,
	li: (chunks: React.ReactNode) => <li css={liCss}>{chunks}</li>,
};

const columnCss = css({
	flex: 1,
	padding: '1.5rem',
	[media.above.sm]: {
		padding: '3rem',
	},
});

const headerCss = css({
	display: 'flex',
	justifyContent: 'center',
	paddingBlock: '1.25rem',
});

const headingCss = css({
	fontSize: '2.5rem',
	fontWeight: 500,
	lineHeight: '2.625rem',
	marginBlockEnd: '1rem',
	marginBlockStart: 0,
});

const subHeadingCss = css({
	borderBlockEnd: `1px solid ${token('color.border')}`,
	fontSize: '1.5rem',
	fontWeight: 500,
	letterSpacing: '.3px',
	lineHeight: 1.33333,
	marginBlockEnd: '1.5rem',
	marginBlockStart: '1.25rem',
	paddingBlockEnd: '0.25rem',
});

const liCss = css({
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '1.375rem',
	listStyleType: 'none',
	marginBlockEnd: 11,
	position: 'relative',
	['::before']: {
		backgroundImage: `url("data:image/svg+xml,%3Csvg width='13px' height='13px' viewBox='0 0 13 13' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg stroke='none' stroke-width='1' fill='none' fill-rule='evenodd' stroke-linecap='round'%3E%3Cg transform='translate(0.000000, -51.000000)' stroke='%2336B37E' stroke-width='1.6'%3E%3Cg transform='translate(1.000000, 45.000000)'%3E%3Cg transform='translate(0.000000, 0.416810)'%3E%3Cpolyline points='1.52655666e-16 11.9766645 3.98219585 17 11 7'%3E%3C/polyline%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
		content: '""',
		display: 'inline-block',
		height: 13,
		insetBlockStart: 11,
		insetInlineStart: '-1.25rem',
		position: 'absolute',
		width: 13,
	},
});

const rowCss = css({
	borderBlock: `1px solid ${token('color.border')}`,
	display: 'flex',
	flexDirection: 'column-reverse',
	margin: '0 auto',
	maxWidth: '62.25rem',
	[media.above.sm]: {
		borderInline: `1px solid ${token('color.border')}`,
		flexDirection: 'row',
		width: '83%',
	},
});

const ulCss = css({
	padding: '0.375rem 0 0.625rem 1.25rem',
});
